import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompletedActions } from "@/hooks/useCompletedActions";
import { ActionPlanTab } from "@/components/report/ActionPlanTab";
import type { ActionItem, AnalysisResult } from "@/types/analysis";
import { Loader2, ClipboardList } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

interface AnalysisGroup {
  url: string;
  date: string;
  actions: ActionItem[];
}

export default function ActionPlanPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<AnalysisGroup[]>([]);
  const [loading, setLoading] = useState(true);
  usePageMeta({ title: "Action Plan — Unranked" });
  const completedActions = useCompletedActions();

  useEffect(() => {
    if (!user) return;

    const fetch = async () => {
      const { data } = await supabase
        .from("analyses")
        .select("url, results, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (data) {
        const mapped: AnalysisGroup[] = data
          .map((row) => {
            const results = row.results as unknown as AnalysisResult;
            return {
              url: row.url,
              date: row.created_at,
              actions: results?.actionPlan ?? [],
            };
          })
          .filter((g) => g.actions.length > 0);

        setGroups(mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [user]);

  if (loading || completedActions.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalActions = groups.reduce((sum, g) => sum + g.actions.length, 0);

  if (totalActions === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">No action items yet</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Run an analysis to generate your action plan.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Action Plan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All action items across your analyses in one place.
        </p>
      </div>

      {groups.map((group, idx) => (
        <div key={idx} className="space-y-2">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold truncate">{group.url}</h2>
            <span className="text-xs text-muted-foreground shrink-0">
              {new Date(group.date).toLocaleDateString()}
            </span>
          </div>
          <ActionPlanTab
            data={group.actions}
            completedActions={completedActions}
          />
        </div>
      ))}
    </div>
  );
}
